/**
 * Project Bobcat, Proposal 1 — the Solution → Agent vocabulary, behind a flag.
 *
 * "Say it in the market's language, in the product not just to clients." The
 * product calls its unit a **Solution**; the market calls it an **Agent**. This
 * module supplies the second vocabulary as a REVERSIBLE overlay so the rename
 * ships behind `bobcat-agent-vocabulary` like every other Bobcat surface.
 *
 * ## Why an i18n overlay and not edited copy
 *
 * The obvious approach — rewrite the strings — was tried and reverted: it is
 * unconditional. A copy change that cannot be switched off rides along on any
 * merge of this branch, and the vocabulary is still awaiting product sign-off.
 *
 * So instead the base copy is left EXACTLY as it ships, and turning the flag on
 * installs an override bundle over it. Flag off is not "the rename undone"; it
 * is the original strings, untouched, because nothing was edited.
 *
 * ## Why a transform rather than a hand-written map
 *
 * The rename is a pure word substitution — Solution→Agent, solutions→agents —
 * applied to every user-facing string. A hand-maintained map of ~100 phrases
 * would drift the moment anyone edits a string. Walking the loaded bundles and
 * substituting reproduces the intended rename exactly, and keeps working as
 * copy changes.
 *
 * The one thing it cannot see is copy that is NOT in i18n. Copy that used to be
 * hardcoded in JSX has therefore been moved into the locale files under a
 * `vocabulary.*` key, in its SHIPPED (Solution) wording — so it lives in a
 * bundle like everything else and the transform reaches it. That move is also
 * what makes those keys typecheck: `TranslationKeys` is generated from the
 * locale JSON, so a key that exists only at runtime is a type error.
 */

/** Namespaces the overlay walks. Must match `getScopeNameSpaces('pro-studio')`. */
export const PRO_STUDIO_NAMESPACES = [
  'pro-studio/common',
  'pro-studio/homepage',
  'pro-studio/versions',
  'pro-studio/channels',
  'pro-studio/actions',
  'pro-studio/navigation',
  'pro-studio/files',
  'pro-studio/channel-details',
  'pro-studio/workflows',
  'pro-studio/performance',
  'pro-studio/keyboard-shortcuts',
  'pro-studio/pype-designer',
] as const;

/**
 * The substitution. Word-boundary anchored and case-preserving across the four
 * forms that actually occur, so `Solutions Using This Connection` and
 * `your solution's expert` both read correctly.
 *
 * Deliberately NOT a bare `replace('Solution','Agent')`: that would also rewrite
 * identifiers embedded in copy (`solutionVersionId` in a debug string) and the
 * middle of unrelated words.
 */
const substitute = (text: string): string =>
  text
    .replace(/\bSolutions\b/g, 'Agents')
    .replace(/\bsolutions\b/g, 'agents')
    .replace(/\bSolution\b/g, 'Agent')
    .replace(/\bsolution\b/g, 'agent');

/**
 * i18next interpolation spans (`{{count}}`, `{{solution}}`) — matched so the
 * substitution can skip them. A placeholder's name is ADDRESSING, not copy: the
 * call site passes `{ solution: … }`, so rewriting `{{solution}}` to
 * `{{agent}}` leaves i18next with no value to interpolate and the literal
 * braces render on screen — and only with the flag on, which is the worst way
 * to find out. No pro-studio bundle carries such a placeholder today; this
 * makes sure adding one cannot break the overlay.
 */
const INTERPOLATION_SPAN = /(\{\{[^{}]*\}\})/g;

export const toAgentVocabulary = (text: string): string =>
  text
    .split(INTERPOLATION_SPAN)
    .map((part) => (part.startsWith('{{') ? part : substitute(part)))
    .join('');

/**
 * Renames the transform cannot express.
 *
 * `toAgentVocabulary` is a Solution→Agent word substitution, so it only reaches
 * strings containing that word. Part of the same vocabulary shift is renaming
 * the **Workflows** nav item to **Agent Workflow** — no "Solution" in sight, so
 * it needs saying explicitly.
 *
 * Applied AFTER the transform, keyed by namespace and then by the same nested
 * path the locale file uses. Add to this rather than widening the substitution:
 * a rule broad enough to catch "Workflows" would also rewrite the Workflows
 * TAB, the workflows table's own copy, and every mention of a workflow as a
 * concept — which are not being renamed.
 */
export const EXPLICIT_OVERRIDES: Record<string, Record<string, unknown>> = {
  'pro-studio/navigation': {
    nav_links: { workflows: 'Agent Workflow' },
  },
};

/** Deep-merge `patch` into a copy of `base` (objects only; leaves overwrite). */
export const deepMerge = (
  base: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...base };
  Object.entries(patch).forEach(([k, v]) => {
    const existing = out[k];
    out[k] =
      v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      existing &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
        ? deepMerge(
            existing as Record<string, unknown>,
            v as Record<string, unknown>
          )
        : v;
  });
  return out;
};

/** Recursively map every string leaf of a resource tree. */
export const mapStringLeaves = (
  value: unknown,
  fn: (s: string) => string
): unknown => {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) return value.map((v) => mapStringLeaves(v, fn));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        mapStringLeaves(v, fn),
      ])
    );
  }
  return value;
};

/**
 * Build the override bundle for one namespace: every string leaf run through
 * `toAgentVocabulary`, then any `EXPLICIT_OVERRIDES` for that namespace merged
 * on top (so an explicit rule always wins over the substitution).
 */
export const buildAgentOverride = (
  resources: Record<string, unknown>,
  namespace?: string
): Record<string, unknown> => {
  const substituted = mapStringLeaves(resources, toAgentVocabulary) as Record<
    string,
    unknown
  >;

  const explicit = namespace ? EXPLICIT_OVERRIDES[namespace] : undefined;
  return explicit ? deepMerge(substituted, explicit) : substituted;
};
